import React, { ReactElement, useCallback, useContext, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { ContentSourceRightsMappingFragment } from '~/graphqlTypes/ContentSourceRightsMappingFragment';
import { StreamingRightsFragment } from '~/graphqlTypes/StreamingRightsFragment';
import type { Controls } from '~/hooks/useAsyncModal';
import {
  commercialModelTypes,
  useTypes,
} from '~/components/ContentSources/_ContentSourceDetail/_ContentSourceDetailRightsMappings';
import { BEM } from '@feedfm/feed-util';
import './_ContentSourceRightsMappingModal.scss';
import { ContentSourceDetailsFragment } from '~/graphqlTypes/ContentSourceDetailsFragment';
import { findMatchingMapping } from '~/components/ContentSources/_ContentSourceDetail/useRightsMappingModal';
import { pluralize } from '~/util/strings';
import { AppContext } from '~/AppContext';

export interface _ContentSourceRightsMappingModalProps {
  /** Controls provided by `useAsyncMocal`. */
  modalControls: Controls<ContentSourceRightsMappingFragment>;
  /** The source being edited, used to check for other conflicting rules. */
  source: ContentSourceDetailsFragment;
  /** Callback this modal can invoke to edit an existing conflicting model. */
  editModel: ( initialModel: ContentSourceRightsMappingFragment ) => void;
}

interface EditMessageProps {
  bem: BEM;
  existingMapping: ContentSourceRightsMappingFragment;
  editModel: () => void;
}

export const EditMessage: React.FC<EditMessageProps> = ( { editModel, bem, existingMapping } ) => {
  return (
    <Alert variant="warning">
      This combination of commercial model and use type is already in use by a rule with
      <code className={ BEM.$( bem.el( 'existing' ) ) }>
        commercialModelType={ existingMapping.commercialModelType }, useType={ existingMapping.useType }
      </code>
      and <code>{ pluralize( existingMapping.streamingRights.length, 'streaming right' ) }</code>. You may
      <a className={ BEM.$( bem.el( 'edit-link' ) ) } onClick={ editModel }>
        edit
      </a>
      that mapping instead.
    </Alert>
  );
};

export const _ContentSourceRightsMappingModal: React.FC<_ContentSourceRightsMappingModalProps> = ( props ) => {
  const bem = useMemo( () => new BEM( 'ops-catalog-sources-detail-rights-mappings-modal' ), [] );
  const { streamingRights } = useContext( AppContext );

  const {
    source,
    editModel,
    modalControls: { localModel, save, close, canSave, isNew, updateField, isSaving, serverError },
  } = props;

  const title = isNew ? `Create Rights Mapping` : 'Modify Rights Mapping';

  const [localError, setLocalError] = React.useState<ReactElement | null>( null );

  const checkForConflicts = useCallback(
    ( commercialModelType: string | undefined, useType: string | undefined ) => {
      const existingMapping = findMatchingMapping( source, commercialModelType, useType, localModel?.id );

      setLocalError(
        existingMapping ? (
          <EditMessage bem={ bem } existingMapping={ existingMapping } editModel={ () => editModel( existingMapping ) } />
        ) : null,
      );
    },
    [source, bem, editModel, localModel],
  );

  const updateCommercialModel = ( commercialModelType: string ) => {
    checkForConflicts( commercialModelType, localModel?.useType );
    updateField( 'commercialModelType', commercialModelType );
  };

  const updateUseType = ( useType: string ) => {
    checkForConflicts( localModel?.commercialModelType, useType );
    updateField( 'useType', useType );
  };

  const updateMappedRights = ( newStreamingRights: Array<StreamingRightsFragment> ) => {
    checkForConflicts( localModel?.commercialModelType, localModel?.useType );
    updateField( 'streamingRights', newStreamingRights );
  };

  const serverErrorAlert = serverError && <Alert variant="danger">{ serverError }</Alert>;

  useEffect( () => {
    checkForConflicts( localModel?.commercialModelType, localModel?.useType );
  }, [checkForConflicts, localModel?.commercialModelType, localModel?.useType] );

  return (
    <Modal show={ true } onHide={ close }>
      <Modal.Header closeButton>
        <Modal.Title>{ title }</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group controlId="commercial-model">
            <Form.Label>Commercial Model</Form.Label>
            <Select
              placeholder="Select commercial model..."
              value={ commercialModelTypes.find( ( { value } ) => value === localModel?.commercialModelType ) }
              options={ commercialModelTypes }
              onChange={ ( commercialModelType ) =>
                commercialModelType && updateCommercialModel( commercialModelType.value )
              }
            />
          </Form.Group>
          <Form.Group controlId="use-type">
            <Form.Label>Use Type</Form.Label>
            <Select
              placeholder="Select use type..."
              value={ useTypes.find( ( { value } ) => value === localModel?.useType ) }
              options={ useTypes }
              onChange={ ( useType ) => useType && updateUseType( useType.value ) }
            />
          </Form.Group>
          <Form.Group controlId="streaming-right">
            <Form.Label>Streaming Right</Form.Label>
            <Select
              value={ localModel?.streamingRights }
              isMulti={ true }
              options={ streamingRights }
              formatOptionLabel={ ( { name, description } ) => (
                <>
                  { name } &mdash; { description }
                </>
              ) }
              getOptionValue={ ( right ) => right.name }
              placeholder="Select one or more streaming rights..."
              onChange={ ( rights ) => updateMappedRights( [...rights] ) }
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        { serverErrorAlert }
        { localError }
        { isSaving && <Spinner animation="border" /> }
        <Button variant="primary" onClick={ close }>
          Cancel
        </Button>
        <Button variant="primary" onClick={ save } disabled={ !canSave }>
          { isNew ? 'Create' : 'Save changes' }
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
